"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";

function getLang(searchParams: ReturnType<typeof useSearchParams>) {
  const lang = searchParams.get("lang");
  return lang === "de" ? "de" : "en";
}

export default function DigitalIntelligencePage() {
  const searchParams = useSearchParams();
  const lang = getLang(searchParams);

  const t =
    lang === "de"
      ? {
          introEyebrow: "Digital Intelligence",
          introTitle: "Strukturierte Systeme und operative Klarheit",
          introText:
            "FL-Care verbindet Daten, Prozesse und digitale Systeme zu einer klaren operativen Logik für spezialisierte Anwendungen und Projekte.",
          cards: [
            {
              title: "Data Structure",
              text: "Strukturierte Informationen als Grundlage für Entscheidungen.",
            },
            {
              title: "Process Logic",
              text: "Klare Abläufe, Zuständigkeiten und nachvollziehbare Schritte.",
            },
            {
              title: "Operational Oversight",
              text: "Transparenz über Status, Entwicklungen und operative Zusammenhänge.",
            },
          ],
          projectEyebrow: "Projekt",
          projectTitle: "Buha",
          projectSubline:
            "Digitale Prozess- und Finanzintelligenz als strukturiertes Projektfeld",
          projectText:
            "Buha ist innerhalb von Digital Intelligence als Projekt für digitale Prozess-, Daten- und Finanzsysteme eingeordnet. Der Schwerpunkt liegt auf Struktur, Automatisierung, operativer Transparenz und einer klaren Abbildung von Abläufen, Freigaben und Zusammenhängen.",
          logicTitle: "Nicht nur Buchhaltung. Sondern Systemlogik.",
          logicText:
            "Buha wird hier nicht als klassischer Buchhaltungsservice dargestellt, sondern als Projekt für strukturierte Prozess- und Finanzlogik in einem digitalen Umfeld.",
          endTitle: "Lass uns über strukturierte digitale Systeme sprechen.",
          endText:
            "Für Projekte, Partnerschaften und weiterführende Gespräche zu Digital Intelligence und verwandten Systemfeldern.",
          contact: "Kontakt",
          home: "Zur Startseite",
        }
      : {
          introEyebrow: "Digital Intelligence",
          introTitle: "Structured systems and operational clarity",
          introText:
            "FL-Care connects data, processes, and digital systems into a clear operational logic for specialized applications and projects.",
          cards: [
            {
              title: "Data Structure",
              text: "Structured information as the basis for decisions.",
            },
            {
              title: "Process Logic",
              text: "Clear flows, responsibilities, and traceable steps.",
            },
            {
              title: "Operational Oversight",
              text: "Transparency across status, developments, and operational relationships.",
            },
          ],
          projectEyebrow: "Project",
          projectTitle: "Buha",
          projectSubline:
            "Digital process and financial intelligence as a structured project field",
          projectText:
            "Within Digital Intelligence, Buha is positioned as a project focused on digital process, data, and financial systems. Its emphasis lies on structure, automation, operational transparency, and the clear mapping of workflows, approvals, and relationships.",
          logicTitle: "Not only accounting. But system logic.",
          logicText:
            "Buha is not presented here as a traditional accounting service, but as a project for structured process and financial logic in a digital environment.",
          endTitle: "Let’s discuss structured digital systems.",
          endText:
            "For projects, partnerships, and further discussions around Digital Intelligence and related system fields.",
          contact: "Contact",
          home: "Back to homepage",
        };

  return (
    <div className="public-page">
      <section className="technology-grid-section">
        <div className="technology-grid-section__header">
          <div className="technology-grid-section__eyebrow">{t.introEyebrow}</div>
          <h1 className="technology-grid-section__title">{t.introTitle}</h1>
          <p className="technology-grid-section__text">{t.introText}</p>
        </div>

        <div className="technology-grid">
          {t.cards.map((card) => (
            <article key={card.title} className="tech-card">
              <div className="tech-card__body">
                <h3 className="tech-card__title">{card.title}</h3>
                <p className="tech-card__text">{card.text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="showcase-block">
        <div className="showcase-block__content">
          <div className="technology-grid-section__eyebrow">{t.projectEyebrow}</div>
          <div className="showcase-block__title">{t.projectTitle}</div>
          <div className="showcase-block__text">
            <strong>{t.projectSubline}</strong>
          </div>
          <div className="showcase-block__text">{t.projectText}</div>
        </div>
      </section>

      <section className="legal-grid">
        <div className="legal-card">
          <h2>{t.logicTitle}</h2>
          <p>{t.logicText}</p>
        </div>
      </section>

      <section id="contact" className="contact-dark">
        <h2 className="contact-dark__title">{t.endTitle}</h2>
        <p className="contact-dark__text">{t.endText}</p>

        <div className="contact-dark__actions">
          <a href="mailto:info@fl-care.com" className="button button--primary">
            {t.contact}
          </a>
          <Link href={`/?lang=${lang}`} className="button button--ghost">
            {t.home}
          </Link>
        </div>
      </section>
    </div>
  );
}
